package com.eladfinish.contract.analytics

import kotlinx.serialization.Serializable

/**
 * Analytics event describing a Bible search performed by the user.
 */
@Serializable
data class SearchAnalyticsEvent(

    /**
     * The original expression entered by the user.
     */
    val query: String,

    /**
     * The selected search mode.
     *
     * Example:
     * EXACT / REGEX / CONTAINS
     */
    val searchMode: String,

    /**
     * The selected search scope.
     *
     * Example:
     * TORAH / TANACH / CURRENT_BOOK
     */
    val searchPlace: String,

    /**
     * Whether the search includes nikud.
     */
    val withNikud: Boolean,

    /**
     * Whether the search includes teamim.
     */
    val withTeamim: Boolean,

    /**
     * Whether the search is restricted to qri text only.
     */
    val onlyQri: Boolean,

    /**
     * Number of verses returned by the search.
     */
    val resultsCount: Int,

    /**
     * Total search execution duration in milliseconds.
     */
    val durationMs: Long,

    /**
     * Whether the user manually stopped the search before completion.
     */
    val cancelledByUser: Boolean,

    /**
     * Application version that sent the event.
     */
    val appVersion: String,

    /**
     * Platform that sent the event.
     *
     * Example:
     * android / iOS
     */
    val platform: String,

    /**
     * Device model name.
     *
     * Example:
     * Pixel 7 Pro / iPhone 15 Pro
     */
    val deviceModel: String,

    /**
     * Anonymous installation identifier.
     *
     * Used for analytics without exposing personal identity.
     */
    val anonymousInstallationId: String,

    /**
     * Event creation timestamp in ISO-8601 format.
     */
    val timestamp: String,

    /**
     * Where the search came from, one of the [SearchSource] constants.
     *
     * Defaults to [SearchSource.UNKNOWN] so events from app versions that do not send it still
     * deserialize.
     */
    val source: String = SearchSource.UNKNOWN,

    /**
     * Ad-free state when the search ran, one of the [AdFreeState] constants.
     *
     * Optional: app versions that predate it do not send it, and the server stores null for them
     * and for any value it does not know.
     */
    val adFree: String? = null,
)
